import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import Stack from "@mui/material/Stack";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import Button from "@mui/material/Button";
import { Avatar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useStyle } from "./style";

interface IProps {
  onChange: (file: any) => void;
  value: any;
  size?: number;
  /** `inline`: single table row — small circle + compact button in a row */
  variant?: "default" | "inline";
}

const ImageUploadComponent = ({
  onChange,
  value,
  size: sizeProp,
  variant = "default",
}: IProps) => {
  const [selectedFile, setSelectedFile] = useState(value);
  useEffect(() => {
    if (value) {
      setSelectedFile(value);
    }
  }, [value]);
  const { classes } = useStyle();
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const size =
    sizeProp ?? (variant === "inline" ? 40 : 100);

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setSelectedFile(ev.target?.result);
        onChange(ev.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const circle = (
    <div
      style={{
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px dashed black",
        borderRadius: "100%",
        textAlign: "center",
        boxSizing: "border-box",
        flexShrink: 0,
        overflow: "hidden",
      }}
      onDragOver={(e) => e.preventDefault()}
    >
      {selectedFile ? (
        <Avatar
          src={selectedFile}
          sx={{ width: size, height: size }}
          variant="circular"
        />
      ) : (
        <CloudUploadOutlinedIcon
          sx={{
            fontSize: variant === "inline" ? Math.max(18, size * 0.42) : "large",
          }}
          color="primary"
        />
      )}
    </div>
  );

  const fileInput = (
    <input
      type="file"
      accept=".jpg, .jpeg, .png, .gif, .svg"
      onChange={handleFileSelect}
      style={{ display: "none" }}
      ref={inputRef}
    />
  );

  if (variant === "inline") {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        useFlexGap
        sx={{ flexWrap: "nowrap", maxWidth: "100%" }}
      >
        {circle}
        {fileInput}
        <Button
          variant="contained"
          size="small"
          onClick={() => inputRef.current?.click()}
          sx={{
            minWidth: "auto",
            px: 1,
            py: 0.25,
            fontSize: 11,
            lineHeight: 1.2,
            textTransform: "none",
            flexShrink: 0,
            maxWidth: "100%",
            whiteSpace: "nowrap",
          }}
        >
          {t("materials.buttons.selectImage")}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      direction={"column"}
      alignItems={"center"}
      justifyContent={"center"}
      gap={"10px"}
    >
      {circle}
      {fileInput}
      <Button
        variant={"contained"}
        onClick={() => inputRef.current?.click()}
        style={classes.selectBtn}
      >
        {t("materials.buttons.selectImage")}
      </Button>
    </Stack>
  );
};

export { ImageUploadComponent };
